import Bowser, { Parser } from "bowser";
import { Session } from "next-session/lib/types";
import { getCookie, setCookie } from 'cookies-next';
import { CookieBanner, S1DeviceType, S1, S1Search } from '@bluefinenterprises/bluefin-libs';
import { MercheoService, generateSignature, getSession } from '../backend';
import { Post, SubIdType } from '../shared';
import { resolveCountry } from '../frontend';
import { Header, Footer, Posts } from '../components';
import { fetchCategoryBySearchTerm, fetchProductsBySearchTerm } from '../backend/cms.comparison411.api';
import { useEffect, useState } from "react";
import { Sidebar } from "../components/sidebar";
import ReCAPTCHA from "../components/recaptcha";

type Props = { s1: S1; posts: Post[]; countryIsoCode: string | undefined; productsC411: Product[]; categoryC411: Category; productsC411Existed: boolean; };
function IndexPage(props: Props) {
    const { posts } = props;
    const { s1: { domain, partnerId, gdprOptIn, isTest, q, segment, signature, subId, subIdType, device, bfcid, bfkid, semwsEndpoint }, countryIsoCode, productsC411, categoryC411, productsC411Existed } = props;

    //replace no ssr because useEffect runs after the component mounts, which only happens on the client side
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        // This code will only run on the client
        setIsClient(true);
    }, []);
    return (
        <>
            <Header q={q} country={countryIsoCode} />
            <div className='app-container'>
                <div className='serp-container'>
                    {isClient &&
                        <>
                            <div data-s1search="mainline-top" />
                            <div data-s1search="mainline-aylf" />
                            <div data-s1search="mainline-algo" />
                            <div data-s1search="mainline-bottom" />
                        </>}
                    {isClient && <div className='pagination' data-s1search="pagination" />}
                    <div className='dekstop-only'>
                        <Footer />
                    </div>
                </div>

                <div className='sidebar-container'>
                    {isClient && <div data-s1search="sidebar" />}
                    {/* <div className='posts-container'>
                        <p className='posts-title'>
                            Mercheo Latest Articles
                        </p>
                        <div className='posts-divider'>
                            <div />
                        </div>
                        <Posts posts={posts} />
                    </div> */}
                    <Sidebar category={categoryC411} products={productsC411} productsExisted={productsC411Existed} />
                </div>                
            </div>
            <div className='mobile-only'>
                <Footer />
            </div>
            <S1Search
                domain={domain}
                partnerId={partnerId}
                gdprOptIn={gdprOptIn}
                isTest={isTest}
                q={q}
                segment={segment}
                signature={signature}
                subId={subId}
                subIdType={subIdType}
                bfcid={bfcid}
                bfkid={bfkid}
                device={device}
                semwsEndpoint={semwsEndpoint}
            />
            <CookieBanner countryIsoCode={countryIsoCode} />
            <ReCAPTCHA action='homepage' />
        </>
    );
}

export async function getServerSideProps(context: any) {
    const { NEXT_PRIVATE_S1_DEFAULT_SEGMENT, NEXT_PRIVATE_S1_PARTNER_ID, NEXT_PRIVATE_S1_DOMAIN, NEXT_PRIVATE_S1_ACCESS_KEY, NEXT_PRIVATE_S1_IS_LIVE, NEXT_PRIVATE_SEMWS_ENDPOINT } = process.env;
    const { query, req, res } = context;
    const session: Session = await getSession(req, res);
    session.bfcid = query.bfcid || session.bfcid;
    session.bfkid = query.bfkid || session.bfkid;
    setCookie('gdprOptIn', 'true', { req, res });

    const userAgent = req.headers['user-agent'];
    if (!query.q) return redirect('https://www.mercheo.com');
    let deviceShortcut: 'm' | 't' | 'c' | undefined = query?.device;
    const device: string = resolveDevice(userAgent, deviceShortcut);
    const isMobile: boolean = ['mobile', 'tablet'].includes(device);
    const desktopSegment: string = query.segment || NEXT_PRIVATE_S1_DEFAULT_SEGMENT;
    const { subId, subIdType }: { subId?: string, subIdType?: SubIdType } = resolveSubIdAndType(query);
    const segment: string = isMobile ? resolveMobileSegment(subIdType) : desktopSegment;
    const countryIsoCode: string | null = resolveCountry(query?.locale) ?? null;

    const s1: S1 = {
        domain: NEXT_PRIVATE_S1_DOMAIN as string,
        partnerId: NEXT_PRIVATE_S1_PARTNER_ID as string,
        gdprOptIn: getCookie('gdprOptIn', { req, res }) === true,
        isTest: NEXT_PRIVATE_S1_IS_LIVE !== 'true', // the both empty string & false will be test
        q: query.q,
        segment,
        signature: generateSignature(new Date(), NEXT_PRIVATE_S1_ACCESS_KEY as string, query.q),
        subId: subId ?? '',
        subIdType: subIdType ?? '',
        bfcid: session.bfcid ?? '',
        bfkid: session.bfkid ?? '',
        device: device as S1DeviceType,
        semwsEndpoint: NEXT_PRIVATE_SEMWS_ENDPOINT as string,
    };
    const posts: Post[] = await MercheoService.fetchFeaturedPosts();

    // Fetching category and product data from comparison411 logic
    const categoryC411 = await fetchCategoryBySearchTerm(query.q);
    const productsC411 = categoryC411 ? await fetchProductsBySearchTerm(categoryC411.slug) : [];
    const sortByRating = (a: any, b: any) => parseFloat(b.rating) - parseFloat(a.rating);
    const mappedProductsWithNetwork = productsC411
        .filter((a: any) => a.network !== null)
        .sort(sortByRating)
        .map((obj: any) => ({
            ...obj,
            landingPageUrl: `https://clicks.growthhub.com/redirect?p=${obj.id}&w=sc411&s=${subId ?? ''}&st=${subIdType ?? ''}`,
            solidStar: getSolidStar(obj.rating),
            outlinedStar: getOutlinedStar(obj.rating)
        }));
    const productsC411Existed: boolean = mappedProductsWithNetwork.length > 0;
    return { props: { posts, s1, countryIsoCode, productsC411: mappedProductsWithNetwork, productsC411Existed, categoryC411 } };
}

const resolveDevice = (userAgent: string, deviceShortcut: string | undefined): string => {
    const DEVICES: { [key: string]: S1DeviceType } = { m: 'mobile', t: 'tablet', c: 'desktop' };
    let device: string | undefined = undefined;
    if (deviceShortcut) device = DEVICES[deviceShortcut];
    if (!device) {
        try {
            const browser: Parser.Parser = Bowser.getParser(userAgent);
            device = browser?.getPlatform()?.type; // desktop, tablet, mobile
        } catch (e: unknown) {
            if (e instanceof Error) console.error('Failed to parse user agent', e);
        } finally {
            if (!device) device = 'desktop';
        }
    }
    return device;
};

const resolveSubIdAndType = (query: any): { subId?: string, subIdType?: SubIdType } => {
    if (query.gclid) return { subId: query.gclid, subIdType: 'gclid' };
    if (query.msclkid) return { subId: query.msclkid, subIdType: 'msclkid' };
    if (query.fbclid) return { subId: query.fbclid, subIdType: 'fbclid' };
    if (query.ttclid) return { subId: query.ttclid, subIdType: 'ttclid' };
    if (query.tid) return { subId: query.tid, subIdType: 'tid' };
    if (query.gbraid) return { subId: query.gbraid, subIdType: 'gbraid' };
    if (query.wbraid) return { subId: query.wbraid, subIdType: 'wbraid' };
    if (query.cid) return { subId: query.gclid, subIdType: 'cid' };
    return {};
}

const resolveMobileSegment = (type: SubIdType | undefined): string => {
    switch (type) {
        case 'gclid': return 'mercheomobilegoogle';
        case 'msclkid': return 'mercheomobilebing';
        default: return 'mercheomobilebox';
    }
};

const redirect = (destination: string) => ({ redirect: { destination, permanent: false, } });
//C411 logic to get rating stars
const getOutlinedStar = (rating: number) => rating >= 9 ? [] : rating >= 7 ? [0] : [0, 1];
const getSolidStar = (rating: number) => rating >= 9 ? [0, 1, 2, 3, 4] : rating >= 7 ? [0, 1, 2, 3] : [0, 1, 2];

export default IndexPage;
